import {
  boundaryFromPolyline,
  boundaryEnd,
  boundaryLength,
  boundaryPoints,
  boundaryArea,
  distanceToSegment,
  joinBoundaries,
  regionMeasure,
  reverseBoundary,
  JOIN_EPS,
  type Boundary,
  type RegionMeasure,
} from './areaMath'
import type { Drawing2D, Point2, Polyline2D } from './viewTypes'

// ---------------------------------------------------------------------------
// Escolher GEOMETRIA JÁ DESENHADA (o "Pick Lines" dos CAD).
//
// Medir por pontos obriga a acertar em cada vértice à mão; o desenho já
// sabe onde estão as linhas. Este módulo é a parte pura disso:
//
//   - um índice das polilinhas do desenho, com a caixa envolvente de
//     cada uma, para apanhar a que está debaixo do cursor;
//   - os contornos FECHADOS de um elemento (o `localId` que viaja com
//     cada polilinha desde a selecção pelo desenho), que é a "área do
//     elemento";
//   - o encadeamento de troços ABERTOS escolhidos à mão até fecharem
//     uma fronteira.
//
// A pontaria usa os `points` (a tesselação), não os arcos: o desvio
// entre a corda e o arco é a flecha da tesselação, muito abaixo do raio
// de pontaria de uma mão com um rato. A MEDIÇÃO é que usa os arcos (ver
// `areaMath.ts`) - é lá que a diferença conta.
//
// Puro: números, sem DOM, sem React.
// ---------------------------------------------------------------------------

/** Uma polilinha escolhível do desenho. A `key` é estável dentro de um
 *  desenho (camada + tipo de traço + posição na lista): é o que a UI
 *  guarda para saber o que já está escolhido, sem segurar objectos. */
export interface PickTarget {
  key: string
  layer: string
  kind: 'cut' | 'projection'
  index: number
  localId?: number
  closed: boolean
  pl: Polyline2D
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** Índice das polilinhas do desenho visível. Reconstrói-se com o
 *  desenho (não com o zoom): o que está numa camada desligada não se vê,
 *  logo não se escolhe. */
export function buildPickIndex(d: Drawing2D | null | undefined): PickTarget[] {
  const out: PickTarget[] = []
  if (!d || !Array.isArray(d.layers)) return out
  for (const layer of d.layers) {
    const add = (list: Polyline2D[], kind: 'cut' | 'projection'): void => {
      for (let i = 0; i < list.length; i++) {
        const pl = list[i]
        if (!pl || !Array.isArray(pl.points) || pl.points.length < 2) continue
        let minX = Infinity
        let minY = Infinity
        let maxX = -Infinity
        let maxY = -Infinity
        for (const p of pl.points) {
          if (p[0] < minX) minX = p[0]
          if (p[0] > maxX) maxX = p[0]
          if (p[1] < minY) minY = p[1]
          if (p[1] > maxY) maxY = p[1]
        }
        out.push({
          key: `${layer.name}|${kind}|${i}`,
          layer: layer.name,
          kind,
          index: i,
          localId: pl.localId,
          closed: !!pl.closed,
          pl,
          minX,
          minY,
          maxX,
          maxY,
        })
      }
    }
    add(layer.cut ?? [], 'cut')
    add(layer.projection ?? [], 'projection')
  }
  return out
}

/** Distância de um ponto a uma polilinha (à tesselação). */
export function distanceToPolyline(pl: Polyline2D, x: number, y: number): number {
  const pts = pl.points
  if (!pts || pts.length === 0) return Infinity
  if (pts.length === 1) return Math.hypot(x - pts[0][0], y - pts[0][1])
  let best = Infinity
  for (let i = 0; i + 1 < pts.length; i++) {
    const d = distanceToSegment(x, y, pts[i], pts[i + 1])
    if (d < best) best = d
  }
  if (pl.closed && pts.length > 2) {
    const d = distanceToSegment(x, y, pts[pts.length - 1], pts[0])
    if (d < best) best = d
  }
  return best
}

/** Duas distâncias dentro disto contam como empate (metros do desenho).
 *  Um décimo de milímetro: duas linhas mais próximas do que isto são,
 *  para quem aponta com o rato, a mesma linha. */
export const PICK_TIE_EPS = 1e-4

/** A polilinha mais próxima do ponto, dentro do raio. Em caso de empate
 *  ganha o traço de CORTE (é o contorno do que está cortado, que é o que
 *  quem mede uma área quer). */
export function nearestTarget(
  index: PickTarget[],
  x: number,
  y: number,
  radius: number,
): PickTarget | null {
  let best: PickTarget | null = null
  let bestD = Infinity
  for (const t of index) {
    // Pré-filtro pela caixa: uma planta tem milhares de polilinhas e isto
    // corre a cada movimento do rato.
    if (
      x < t.minX - radius ||
      x > t.maxX + radius ||
      y < t.minY - radius ||
      y > t.maxY + radius
    ) {
      continue
    }
    const d = distanceToPolyline(t.pl, x, y)
    if (d > radius) continue
    if (best === null || d < bestD - PICK_TIE_EPS) {
      best = t
      bestD = d
      continue
    }
    if (Math.abs(d - bestD) <= PICK_TIE_EPS && t.kind === 'cut' && best.kind !== 'cut') {
      best = t
      bestD = d
    }
  }
  return best
}

/** Contornos FECHADOS do corte de um elemento. É o que faz a "área do
 *  elemento": a secção cortada, com os furos (que aqui são só mais
 *  contornos fechados do mesmo `localId`, ver `regionMeasure`). */
export function elementLoops(
  d: Drawing2D | null | undefined,
  localId: number,
): Polyline2D[] {
  const out: Polyline2D[] = []
  if (!d || !Array.isArray(d.layers)) return out
  for (const layer of d.layers) {
    for (const pl of layer.cut ?? []) {
      if (!pl.closed || pl.points.length < 3) continue
      if (pl.localId !== localId) continue
      out.push(pl)
    }
  }
  return out
}

/** Área e perímetro da secção cortada de um elemento. */
export function elementMeasure(
  d: Drawing2D | null | undefined,
  localId: number,
): RegionMeasure {
  return regionMeasure(elementLoops(d, localId))
}

// --- Encadear troços escolhidos --------------------------------------------

/** Resultado do encadeamento dos troços escolhidos. */
export interface ChainResult {
  /** Fronteira encadeada (null sem troços). */
  boundary: Boundary | null
  closed: boolean
  /** Comprimento acumulado do que já está escolhido. */
  length: number
  /** Área; 0 enquanto a fronteira não fechar. */
  area: number
  /** Contorno amostrado, para a cota e para o realce. */
  points: Point2[]
  /** Troços que não encostaram a nenhuma ponta e ficaram de fora do
   *  encadeamento (mas contam no comprimento: estão escolhidos). */
  loose: number
}

const EMPTY_CHAIN: ChainResult = {
  boundary: null,
  closed: false,
  length: 0,
  area: 0,
  points: [],
  loose: 0,
}

/** Encadeia os troços escolhidos numa fronteira só.
 *
 *  Guloso e independente da ORDEM dos cliques: parte do primeiro troço e
 *  vai agarrando, dos que sobram, o que encostar a uma das duas pontas
 *  da cadeia (invertendo-o se for preciso). O que não encostar a nada
 *  fica "solto" e é contado à parte, para a UI o poder dizer em vez de
 *  inventar uma fronteira com saltos.
 *
 *  Um troço FECHADO (um círculo, o contorno de um pilar) é uma fronteira
 *  por si só: se o primeiro escolhido for fechado, é ele a resposta e os
 *  outros ficam soltos. */
export function chainPolylines(
  polys: Polyline2D[],
  tol = JOIN_EPS,
): ChainResult {
  if (!Array.isArray(polys) || polys.length === 0) return EMPTY_CHAIN
  const parts: Boundary[] = []
  for (const pl of polys) {
    const b = boundaryFromPolyline(pl)
    if (b) parts.push(b)
  }
  if (parts.length === 0) return EMPTY_CHAIN

  const first = parts[0]
  if (first.closed) {
    return {
      boundary: first,
      closed: true,
      length: boundaryLength(first),
      area: boundaryArea(first),
      points: boundaryPoints(first),
      loose: parts.length - 1,
    }
  }

  let chain = first
  const rest = parts.slice(1)
  const used = new Array<boolean>(rest.length).fill(false)
  let grew = true
  while (grew) {
    grew = false
    for (let i = 0; i < rest.length; i++) {
      if (used[i]) continue
      const piece = rest[i]
      if (piece.closed) continue
      const end = boundaryEnd(chain)
      const start = chain.from
      const pStart = piece.from
      const pEnd = boundaryEnd(piece)
      if (near(end, pStart, tol)) {
        chain = joinBoundaries(chain, piece)
      } else if (near(end, pEnd, tol)) {
        chain = joinBoundaries(chain, reverseBoundary(piece))
      } else if (near(start, pEnd, tol)) {
        chain = joinBoundaries(piece, chain)
      } else if (near(start, pStart, tol)) {
        chain = joinBoundaries(reverseBoundary(piece), chain)
      } else {
        continue
      }
      used[i] = true
      grew = true
      if (chain.closed) break
    }
    if (chain.closed) break
  }

  let loose = 0
  let extra = 0
  for (let i = 0; i < rest.length; i++) {
    if (used[i]) continue
    loose++
    extra += boundaryLength(rest[i])
  }
  const closed = chain.closed
  return {
    boundary: chain,
    closed,
    length: boundaryLength(chain) + extra,
    area: closed ? boundaryArea(chain) : 0,
    points: boundaryPoints(chain),
    loose,
  }
}

function near(a: Point2, b: Point2, tol: number): boolean {
  return Math.hypot(a[0] - b[0], a[1] - b[1]) <= tol
}
